import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FREQUENCY_OPTIONS = [
  { value: 'immediate', label: 'Immediate', desc: 'Notify as soon as a change is detected' },
  { value: 'daily',     label: 'Daily Digest', desc: 'One summary email per day' },
  { value: 'weekly',    label: 'Weekly Digest', desc: 'One summary email per week' },
];

const PLAN_LABELS = {
  free:       { color: 'bg-gray-100 text-gray-700', label: 'Free' },
  basic:      { color: 'bg-blue-100 text-blue-700', label: 'Basic' },
  standard:   { color: 'bg-purple-100 text-purple-700', label: 'Standard' },
  enterprise: { color: 'bg-red-100 text-red-700', label: 'Enterprise' },
};

export default function NotificationSettings({ user, token }) {
  const [prefs, setPrefs]         = useState({ email_enabled: true, notification_frequency: 'immediate' });
  const [history, setHistory]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [testing, setTesting]     = useState(false);
  const [toast, setToast]         = useState(null);

  const canUseEmail = ['basic', 'standard', 'enterprise'].includes(user?.subscription_plan);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const authHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [prefsRes, histRes] = await Promise.all([
        axios.get(`${API}/notifications/preferences`, authHeaders()),
        axios.get(`${API}/notifications/history`, authHeaders()),
      ]);
      setPrefs({ email_enabled: prefsRes.data.email_enabled, notification_frequency: prefsRes.data.notification_frequency });
      setHistory(histRes.data);
    } catch {
      // Silently use defaults on first load
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/notifications/preferences`, prefs, authHeaders());
      showToast('Preferences saved successfully');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to save preferences', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/notifications/test`, {}, authHeaders());
      showToast(res.data.message || 'Test email sent! Check your inbox.');
      fetchData(); // refresh history
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to send test email', 'error');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const planInfo = PLAN_LABELS[user?.subscription_plan] || PLAN_LABELS.free;

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-6 right-6 z-50 px-5 py-3 rounded-lg shadow-lg font-semibold text-sm transition-all
          ${toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-green-600 text-white'}`}>
          {toast.message}
        </div>
      )}

      <div className="mb-6">
        <h2 className="text-2xl font-bold text-blue-900">📧 Notification Settings</h2>
        <p className="text-gray-500 mt-1 text-sm">Manage how and when you receive case status updates.</p>
      </div>

      {/* Plan badge */}
      <div className="flex items-center gap-3 mb-6">
        <span className={`px-3 py-1 rounded-full text-xs font-bold ${planInfo.color}`}>
          {planInfo.label} Plan
        </span>
        {!canUseEmail && (
          <span className="text-sm text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1 rounded-full">
            Upgrade to Basic or higher to enable email notifications
          </span>
        )}
      </div>

      {/* Email toggle card */}
      <div className={`bg-white rounded-xl shadow border-2 p-6 mb-5 transition-opacity ${!canUseEmail ? 'opacity-60' : ''}`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-bold text-gray-800 text-lg">📬 Email Notifications</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Receive an email when your USCIS case status changes.
            </p>
          </div>
          <label className={`relative inline-flex items-center ${!canUseEmail ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
            <input
              type="checkbox"
              className="sr-only peer"
              checked={prefs.email_enabled}
              disabled={!canUseEmail}
              onChange={e => setPrefs(p => ({ ...p, email_enabled: e.target.checked }))}
            />
            <div className="w-12 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer
                            peer-checked:after:translate-x-full peer-checked:after:border-white
                            after:content-[''] after:absolute after:top-[2px] after:left-[2px]
                            after:bg-white after:border-gray-300 after:border after:rounded-full
                            after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600">
            </div>
          </label>
        </div>

        {/* Frequency selector */}
        <div className={prefs.email_enabled && canUseEmail ? '' : 'pointer-events-none opacity-50'}>
          <p className="text-sm font-semibold text-gray-700 mb-2">Notification Frequency</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {FREQUENCY_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setPrefs(p => ({ ...p, notification_frequency: opt.value }))}
                className={`text-left border-2 rounded-lg p-3 transition-all
                  ${prefs.notification_frequency === opt.value
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300'}`}
              >
                <div className="font-semibold text-sm text-gray-800">{opt.label}</div>
                <div className="text-xs text-gray-500 mt-0.5">{opt.desc}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-3 mb-8">
        <button
          onClick={handleSave}
          disabled={saving || !canUseEmail}
          className="bg-blue-700 hover:bg-blue-800 disabled:opacity-50 text-white font-semibold
                     px-6 py-2.5 rounded-lg transition-colors"
        >
          {saving ? 'Saving…' : 'Save Preferences'}
        </button>
        <button
          onClick={handleTestEmail}
          disabled={testing || !canUseEmail || !prefs.email_enabled}
          className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-semibold
                     px-6 py-2.5 rounded-lg transition-colors"
        >
          {testing ? 'Sending…' : '✉️ Send Test Email'}
        </button>
      </div>

      {/* Notification history */}
      <div className="bg-white rounded-xl shadow border-2 p-6">
        <h3 className="font-bold text-gray-800 text-lg mb-4">📋 Notification History</h3>
        {history.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-6">No notifications sent yet.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {history.map(rec => (
              <div key={rec.id} className="py-3 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full
                      ${rec.status === 'sent'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700'}`}>
                      {rec.status === 'sent' ? '✓ Sent' : '✗ Failed'}
                    </span>
                    <span className="text-xs text-gray-400 capitalize">{rec.trigger.replace(/_/g, ' ')}</span>
                  </div>
                  <p className="text-sm text-gray-700 mt-1 truncate">{rec.subject}</p>
                  <p className="text-xs text-gray-400">{rec.recipient_email}</p>
                </div>
                <div className="text-xs text-gray-400 whitespace-nowrap shrink-0">
                  {new Date(rec.sent_at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
